import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, interviewTypeToApiValue } from '@/services/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { TeamMember, Interview } from '@/types';

export interface UseInterviewSchedulingReturn {
  date: Date | undefined;
  setDate: (v: Date | undefined) => void;
  time: string;
  setTime: (v: string) => void;
  /** End time (HH:mm, same day as `date`) when Google Calendar is off — duration is derived */
  endTime: string;
  setEndTime: (v: string) => void;
  /** Minutes computed from time→endTime when external-only; null if invalid */
  derivedDurationMinutes: number | null;
  notes: string;
  setNotes: (v: string) => void;
  /** Zoom / Meet / Teams URL — Calendar integration disabled in UI for now */
  externalMeetingUrl: string;
  setExternalMeetingUrl: (v: string) => void;
  participantSearch: string;
  setParticipantSearch: (v: string) => void;
  participants: { userId: string; name: string; email: string; role?: string; team?: string }[];
  submitting: boolean;
  success: boolean;
  meetLink: string;
  calendarLink: string;
  teamMembers: TeamMember[];
  filteredMembers: TeamMember[];
  canSubmit: boolean;
  addParticipant: (member: TeamMember) => void;
  removeParticipant: (userId: string) => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
  isEditMode: boolean;
}

/** Google Calendar + Meet from this modal — not available yet (API: createGoogleCalendar false) */
const CREATE_GOOGLE_CALENDAR = false;

function isValidHttpMeetingUrl(s: string): boolean {
  const t = s.trim();
  return t.length > 0 && /^https?:\/\//i.test(t);
}

function formatNowTime(): string {
  const d = new Date();
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function addMinutesToHHmm(hhmm: string, addMinutes: number): string {
  const m = hhmm.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return '11:00';
  let h = Number(m[1]);
  let mi = Number(m[2]);
  let total = h * 60 + mi + addMinutes;
  total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const nh = Math.floor(total / 60);
  const nmi = total % 60;
  return `${nh.toString().padStart(2, '0')}:${nmi.toString().padStart(2, '0')}`;
}

/** Duration in minutes from start/end local times on `day`; if end ≤ start, assumes end is next calendar day. */
function computeDurationMinutesFromTimes(day: Date, startHHmm: string, endHHmm: string): number | null {
  const ts = startHHmm.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  const te = endHHmm.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!ts || !te) return null;
  const sh = Number(ts[1]);
  const sm = Number(ts[2]);
  const eh = Number(te[1]);
  const em = Number(te[2]);
  const s = new Date(day);
  s.setHours(sh, sm, 0, 0);
  const e = new Date(day);
  e.setHours(eh, em, 0, 0);
  let diffMin = (e.getTime() - s.getTime()) / 60_000;
  if (diffMin <= 0) diffMin += 24 * 60;
  if (diffMin <= 0) return null;
  return Math.round(diffMin);
}

export function useInterviewScheduling(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  companyId: string,
  positionId: string,
  applicationId: string,
  assignedMemberIds?: string[],
  onScheduled?: () => void,
  mode: 'create' | 'edit' = 'create',
  editInterview?: Interview | null,
): UseInterviewSchedulingReturn {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isEditMode = mode === 'edit' && !!editInterview;

  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState(() => formatNowTime());
  const [endTime, setEndTime] = useState(() => addMinutesToHHmm(formatNowTime(), 60));
  /** Kept for API / validation only (no type picker in UI) */
  const [type, setType] = useState('video_call');
  const [notes, setNotes] = useState('');
  const [enableTranscription, setEnableTranscription] = useState(true);
  const [externalMeetingUrl, setExternalMeetingUrl] = useState('');
  const [participantSearch, setParticipantSearch] = useState('');
  const [participants, setParticipants] = useState<{ userId: string; name: string; email: string; role?: string; team?: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [meetLink, setMeetLink] = useState('');
  const [calendarLink, setCalendarLink] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (!open || !companyId) return;
    api.team.list(companyId).then(members => {
      setTeamMembers(members);
      if (mode !== 'edit' && assignedMemberIds && assignedMemberIds.length > 0) {
        setParticipants(prev => {
          if (prev.length > 0) return prev;
          const assigned = members
            .filter(m => assignedMemberIds.includes(m.userId))
            .map(m => ({
              userId: m.userId,
              name: m.displayName,
              email: m.email,
              role: m.jobRole ?? undefined,
              team: m.teamIds?.[0],
            }));
          return assigned.length > 0 ? assigned : prev;
        });
      }
    }).catch(() => {});
  }, [open, companyId, mode, assignedMemberIds?.join(',')]);

  /** Create mode: default date = today, time = now, end = +1h when opening */
  useEffect(() => {
    if (!open || isEditMode) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setDate((d) => d ?? new Date(today));
    const t = formatNowTime();
    setTime(t);
    setEndTime(addMinutesToHHmm(t, 60));
  }, [open, isEditMode]);

  useEffect(() => {
    if (!open || !isEditMode || !editInterview) return;
    const d = new Date(editInterview.scheduledAt);
    if (!Number.isNaN(d.getTime())) {
      setDate(d);
      setTime(`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`);
    }
    setType(interviewTypeToApiValue(editInterview.type));
    setNotes(editInterview.notes ?? '');
    setSuccess(false);
    setMeetLink(editInterview.meetLink ?? '');
    setCalendarLink(editInterview.calendarLink ?? '');
    setEnableTranscription(editInterview.enableTranscription ?? true);
    setExternalMeetingUrl(editInterview.meetLink ?? '');
    const startMs = new Date(editInterview.scheduledAt).getTime();
    const end = new Date(startMs + editInterview.duration * 60 * 1000);
    setEndTime(
      `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`,
    );
  }, [open, isEditMode, editInterview?.id, editInterview?.scheduledAt, editInterview?.duration, editInterview?.type, editInterview?.enableTranscription, editInterview?.meetLink]);

  useEffect(() => {
    if (!open || !isEditMode || !editInterview || teamMembers.length === 0) return;
    const memberParts = editInterview.participants.filter(p => !p.isCandidate);
    const next = memberParts
      .map(p => {
        const uid =
          p.userId
          ?? teamMembers.find(tm => tm.email.toLowerCase() === p.email.toLowerCase())?.userId
          ?? '';
        return { userId: uid, name: p.name, email: p.email };
      })
      .filter(p => p.userId);
    if (next.length > 0) setParticipants(next);
  }, [open, isEditMode, editInterview?.id, teamMembers, editInterview?.participants]);

  const filteredMembers = teamMembers.filter(m =>
    !participants.some(p => p.userId === m.userId) &&
    (m.displayName.toLowerCase().includes(participantSearch.toLowerCase()) ||
     m.email.toLowerCase().includes(participantSearch.toLowerCase()))
  );

  const derivedDurationMinutes = useMemo(() => {
    if (!date) return null;
    return computeDurationMinutesFromTimes(date, time, endTime);
  }, [date, time, endTime]);

  const needsExternalMeetingUrl = type === 'video_call';
  const participantIdsForSubmit =
    participants.length > 0
      ? participants.map(p => p.userId)
      : user?.id
        ? [user.id]
        : [];

  const canSubmit =
    !!date &&
    !!time &&
    /^([01]?\d|2[0-3]):[0-5]\d$/.test(time.trim()) &&
    participantIdsForSubmit.length > 0 &&
    (!needsExternalMeetingUrl || isValidHttpMeetingUrl(externalMeetingUrl)) &&
    /^([01]?\d|2[0-3]):[0-5]\d$/.test(endTime.trim()) &&
    derivedDurationMinutes !== null &&
    derivedDurationMinutes > 0;

  const addParticipant = useCallback((member: TeamMember) => {
    setParticipants(prev => [...prev, { userId: member.userId, name: member.displayName, email: member.email, role: member.jobRole ?? undefined, team: member.teamIds?.[0] || '' }]);
    setParticipantSearch('');
  }, []);

  const removeParticipant = useCallback((userId: string) => {
    setParticipants(prev => prev.filter(p => p.userId !== userId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !date) return;
    setSubmitting(true);

    const [hours, minutes] = time.split(':').map(Number);
    const scheduledDate = new Date(date);
    scheduledDate.setHours(hours, minutes, 0, 0);
    const runId = editInterview?.pipelineRunId ?? editInterview?.id;
    const pis = participantIdsForSubmit;
    const durationMinutesResolved = derivedDurationMinutes ?? 0;

    try {
      if (isEditMode && runId) {
        const result = await api.applications.updateInterview(
          companyId,
          positionId,
          applicationId,
          runId,
          {
            scheduledDate: scheduledDate.toISOString(),
            participants: pis,
            durationMinutes: durationMinutesResolved,
            interviewType: type,
            notes: notes || undefined,
            sendCustomEmail: false,
            sendGoogleInvite: CREATE_GOOGLE_CALENDAR,
            createGoogleCalendar: CREATE_GOOGLE_CALENDAR,
            externalMeetingUrl: externalMeetingUrl.trim(),
            enableTranscription: enableTranscription,
          },
        );
        const collected = result.collectedData || {};
        setMeetLink((collected.meet_link?.value as string) || result.interviews?.find(i => i.id === runId || i.pipelineRunId === runId)?.meetLink || '');
        setCalendarLink((collected.event_html_link?.value as string) || result.interviews?.find(i => i.id === runId || i.pipelineRunId === runId)?.calendarLink || '');
      } else {
        const result = await api.applications.decide(companyId, positionId, applicationId, 'interview', {
          scheduledDate: scheduledDate.toISOString(),
          participants: pis,
          durationMinutes: durationMinutesResolved,
          interviewType: type,
          notes: notes || undefined,
          sendCustomEmail: false,
          sendGoogleInvite: CREATE_GOOGLE_CALENDAR,
          createGoogleCalendar: CREATE_GOOGLE_CALENDAR,
          externalMeetingUrl: externalMeetingUrl.trim(),
          enableTranscription: true,
        });

        const collected = result.collectedData || {};
        setMeetLink((collected.meet_link?.value as string) || '');
        setCalendarLink((collected.event_html_link?.value as string) || '');
      }

      setSubmitting(false);
      setSuccess(true);
      onScheduled?.();
    } catch {
      setSubmitting(false);
      toast.error(t('interviewModal.failed'));
    }
  }, [canSubmit, date, time, endTime, derivedDurationMinutes, type, notes, enableTranscription, externalMeetingUrl, participantIdsForSubmit, companyId, positionId, applicationId, onScheduled, t, isEditMode, editInterview]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(() => {
      setDate(undefined);
      setTime(formatNowTime());
      setEndTime(addMinutesToHHmm(formatNowTime(), 60));
      setType('video_call');
      setNotes('');
      setEnableTranscription(true);
      setExternalMeetingUrl('');
      setParticipants([]);
      setSuccess(false);
      setMeetLink('');
      setCalendarLink('');
    }, 300);
  }, [onOpenChange]);

  return {
    date, setDate, time, setTime, endTime, setEndTime, derivedDurationMinutes,
    notes, setNotes,
    externalMeetingUrl, setExternalMeetingUrl,
    participantSearch, setParticipantSearch, participants,
    submitting, success, meetLink, calendarLink,
    teamMembers, filteredMembers, canSubmit,
    addParticipant, removeParticipant, handleSubmit, handleClose,
    isEditMode,
  };
}
